import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from "react";
import { SearchRegular, ArrowSyncRegular, AddRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { ProjectsApi } from "@/services/api/projects-api";
import { getCreateDealDisplay, openCreateDealDialog } from "@/services/outlook/create-deal-dialog";
import type { TriyoProject } from "@/types/models";
import { formatLongCalendarDate } from "@/utils/calendar-date";
import { TabSectionHeader } from "@/components/Workflow/TabSectionHeader";
import { CreateDeal } from "@/components/Workflow/QuickActions/CreateDeal";
import "../shared.scss";
import "./Deals.scss";

function projectName(project: TriyoProject): string {
  return project.Name || project.ProjectName || project.Title || "Untitled deal";
}

function projectEndDate(project: TriyoProject): string {
  const raw = project.EndDate || project.DueDate || project.End;
  if (!raw) return "—";
  return formatLongCalendarDate(raw);
}

/** ProjectStatus enum from the API: Active = 1, InActive = 0, Completed = 2. */
const PROJECT_STATUS = { inactive: 0, active: 1, completed: 2 } as const;

const INACTIVE_STATUSES = new Set([
  "archived",
  "completed",
  "complete",
  "inactive",
  "in active",
  "closed",
  "cancelled",
  "canceled",
]);

type DealFilter = "active" | "all";

const DEAL_FILTERS: { id: DealFilter; label: string }[] = [
  { id: "active", label: "Active" },
  { id: "all", label: "All" },
];

/** Rows per request. The API pages server-side via Skip/Take. */
const PAGE_SIZE = 25;

function isActiveProject(project: TriyoProject): boolean {
  if (project.Deleted === true) return false;
  if (typeof project.StatusId === "number") return project.StatusId === PROJECT_STATUS.active;
  const status = (project.StrStatus || project.StatusName || project.Status || "").trim().toLowerCase();
  return !status || !INACTIVE_STATUSES.has(status);
}

function ownerInitials(project: TriyoProject): string {
  if (project.OwnerInitials) return project.OwnerInitials.slice(0, 2).toUpperCase();
  const name = project.OwnerName || "";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Deals(): JSX.Element {
  const [projects, setProjects] = useState<TriyoProject[]>([]);
  const [page, setPage] = useState(1);
  const [exhausted, setExhausted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<DealFilter>("active");
  const [query, setQuery] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [creating, setCreating] = useState(false);
  const requestRef = useRef(0);

  // `reset` restarts at page 1; otherwise the next page is appended. The API
  // returns a bare array with no total, so a short page means we've reached the end.
  const load = useCallback(async (nextPage: number, reset: boolean) => {
    const requestId = ++requestRef.current;
    if (reset) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const rows = await ProjectsApi.getProjectPage({ pageNumber: nextPage, pageSize: PAGE_SIZE });
      if (requestId !== requestRef.current) return;
      setPage(nextPage);
      setExhausted(rows.length < PAGE_SIZE);
      setProjects((current) => (reset ? rows : [...current, ...rows]));
    } catch (reason: unknown) {
      if (requestId !== requestRef.current) return;
      if (reset) setProjects([]);
      setError(reason instanceof Error ? reason.message : "Deals could not be loaded.");
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    void load(1, true);
    return () => {
      requestRef.current += 1;
    };
  }, [load]);

  const startCreating = () => {
    if (getCreateDealDisplay() === "dialog") {
      void openCreateDealDialog().then((result) => {
        if (result === "unavailable") setCreating(true);
      });
      return;
    }
    setCreating(true);
  };

  const deals = useMemo(() => {
    // The API accepts a filters array but ignores it, so status stays client-side.
    const list = filter === "active" ? projects.filter(isActiveProject) : projects;
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => {
      const name = projectName(p).toLowerCase();
      const company = (p.CompanyName ?? "").toLowerCase();
      return name.includes(q) || company.includes(q);
    });
  }, [projects, filter, query]);

  const refresh = async () => {
    setSpinning(true);
    try {
      await load(1, true);
    } finally {
      setSpinning(false);
    }
  };

  if (creating) {
    return (
      <div className="tab-panel">
        <TabSectionHeader
          title="Create Deal"
          action={
            <button type="button" className="deals__back" onClick={() => setCreating(false)}>
              Back
            </button>
          }
        />
        <CreateDeal onClose={() => setCreating(false)} onCreated={() => void load(1, true)} />
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <TabSectionHeader
        title="Deals"
        action={
          <button
            type="button"
            aria-label="Refresh"
            title="Refresh"
            className={`icon-button${spinning ? " icon-button--spin" : ""}`}
            disabled={spinning}
            aria-busy={spinning}
            onClick={() => void refresh()}
          >
            <ArrowSyncRegular />
          </button>
        }
      />

      <div className="search-row">
        <div className="search-field">
          <SearchRegular />
          <input
            className="search-field__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for Deals"
            aria-label="Search for Deals"
          />
        </div>
        <button type="button" className="add-button" aria-label="New Deal" title="New Deal" onClick={startCreating}>
          <AddRegular />
        </button>
      </div>

      <div className="list-tabs" role="tablist" aria-label="Deal status">
        {DEAL_FILTERS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={filter === id}
            className={`list-tab${filter === id ? " list-tab--active" : ""}`}
            onClick={() => setFilter(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="list-scroll">
        {loading ? (
          <div className="empty-state" role="status" aria-live="polite">
            <FluentSpinner label="Loading deals…" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="empty-state" role="alert">
            {error}
          </div>
        ) : null}

        {!loading && !error && deals.length === 0 ? (
          <div className="empty-state" role="status">
            No Deals{query ? " match your search" : ""}
          </div>
        ) : null}

        {!loading &&
          !error &&
          deals.map((deal) => {
            const initials = ownerInitials(deal);
            return (
              <article key={deal.Id ?? deal.ProjectId ?? projectName(deal)} className="deal-card">
                <div className="deal-card__name">{projectName(deal)}</div>
                <div className="deal-card__meta">
                  <span className="deal-card__date">End Date: {projectEndDate(deal)}</span>
                  {initials ? <span className="deal-card__avatar">{initials}</span> : null}
                </div>
              </article>
            );
          })}

        {!loading && !error && !exhausted ? (
          <button type="button" className="load-more" disabled={loadingMore} onClick={() => void load(page + 1, false)}>
            {loadingMore ? "Loading…" : `Load more (${projects.length} loaded)`}
          </button>
        ) : null}
      </div>
    </div>
  );
}
